import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "../lib/trpc";
import Header from "../components/Header";
import Footer from "../components/Footer";
import StoreCard from "../components/StoreCard";

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: stores, isLoading: storesLoading } = trpc.stores.list.useQuery({
    limit: 6,
  });

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      {/* ヒーローセクション - Apple風フルスクリーン */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* 背景画像 */}
        <div className="absolute inset-0">
          <img
            src="/hero-main.jpg"
            alt="ベビーカーを横付けして食事する親の笑顔"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        </div>

        {/* コンテンツ */}
        <div className="relative z-10 container text-center px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-white/80 text-sm md:text-base font-medium tracking-widest uppercase mb-6 animate-fade-in-up">
              SONOMAMA
            </p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-[1.1] animate-fade-in-up animate-delay-100">
              「行ってみたら<br className="sm:hidden" />入れなかった」<br />をなくす。
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animate-delay-200">
              入店難易度、通路幅、設備情報まで徹底的に可視化。<br className="hidden md:block" />
              安心して行けるお店が見つかります。
            </p>

            {/* 検索窓 - Apple風ミニマル */}
            <div className="max-w-xl mx-auto animate-fade-in-up animate-delay-300">
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-2 shadow-2xl">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <svg
                      className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      placeholder="エリア、駅名、店舗名で検索..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full h-14 pl-12 pr-4 text-base border-0 bg-transparent focus:outline-none focus:ring-0 text-stone-900 placeholder:text-stone-400"
                    />
                  </div>
                  <Link
                    href={`/stores?q=${encodeURIComponent(searchQuery)}`}
                    className="h-14 px-8 rounded-xl bg-stone-900 hover:bg-stone-800 text-white font-medium flex items-center justify-center transition-colors"
                  >
                    検索
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* スクロールインジケーター */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/80 rounded-full" />
          </div>
        </div>
      </section>

      {/* ショートカット検索 - Apple風カードグリッド */}
      <section className="py-20 md:py-32 bg-stone-100">
        <div className="container">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold text-stone-900 mb-4 tracking-tight">
              今の気分と状況から探す
            </h3>
            <p className="text-stone-500 text-lg">
              シチュエーションに合わせて最適なお店を見つけましょう
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              {
                href: "/stores?strollerEntry=true",
                color: "from-teal-500 to-teal-600",
                title: "そのまま入店OK",
                desc: "ベビーカー横付け可能",
                icon: (
                  <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <circle cx="12" cy="6" r="3" />
                    <path d="M12 9v4M8 21l2-8h4l2 8M6 13h12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
              },
              {
                href: "/stores?isRainyDayOk=true",
                color: "from-slate-500 to-slate-600",
                title: "雨に濡れずに行ける",
                desc: "駅直結・地下道",
                icon: (
                  <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                ),
              },
              {
                href: "/stores?hasElevator=true",
                color: "from-emerald-500 to-emerald-600",
                title: "エレベーターあり",
                desc: "階段なしでアクセス",
                icon: (
                  <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 8v8M16 12h.01" />
                  </svg>
                ),
              },
              {
                href: "/stores?hasDiaperChanging=true",
                color: "from-violet-500 to-violet-600",
                title: "オムツ替え完備",
                desc: "慌てなくて大丈夫",
                icon: (
                  <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                ),
              },
            ].map((item, i) => (
              <Link key={i} href={item.href}>
                <div className="group cursor-pointer border-0 bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full rounded-3xl overflow-hidden">
                  <div className="p-6 md:p-8">
                    <div
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
                    >
                      {item.icon}
                    </div>
                    <h4 className="font-semibold text-lg text-stone-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-stone-500">{item.desc}</p>
                    <div className="mt-4 flex items-center text-sm font-medium text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      探す
                      <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* おすすめ店舗 */}
      <section className="py-20 md:py-32">
        <div className="container">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <h3 className="text-3xl md:text-4xl font-semibold text-stone-900 tracking-tight">
                おすすめの店舗
              </h3>
              <p className="text-stone-500 mt-2">ベビーカーで行きやすいお店をピックアップ</p>
            </div>
            <Link
              href="/stores"
              className="text-stone-600 hover:text-stone-900 font-medium flex items-center gap-2 group"
            >
              すべて見る
              <svg
                className="h-4 w-4 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {storesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/5] bg-stone-200 rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : !stores?.items?.length ? (
            <div className="text-center py-20 bg-white rounded-3xl shadow-sm">
              <p className="text-stone-500 mb-6">まだ店舗が登録されていません</p>
              <Link href="/admin" className="btn-primary">
                最初の店舗を登録する
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {stores.items.map((store: any) => (
                <StoreCard key={store.id} store={store} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA セクション */}
      <section className="py-20 md:py-32">
        <div className="container">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-500 via-teal-600 to-amber-400 p-12 md:p-20 text-center">
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 tracking-tight">
                お店の情報を登録しませんか？
              </h3>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                ベビーカーで来店しやすいお店の情報を共有して、<br className="hidden md:block" />
                子育て中の親御さんを応援しましょう。
              </p>
              <Link
                href="/admin"
                className="inline-flex items-center justify-center h-14 px-10 rounded-full bg-white text-stone-900 hover:bg-white/90 font-medium transition-colors"
              >
                店舗を登録する
              </Link>
            </div>
            {/* 装飾 */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
